import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead
import { useBooking } from '../../../../contexts/BookingContext';
import PaymentSenseConnectE from '../../../common/PaymentSenseConnectE';
import { venposService } from '../../../../services/venposService';

// Error handling removed for production security

const PaymentContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const PaymentSection = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
`;


const SectionHeader = styled.h3`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;


const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;


const OrderSummary = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid ${props => props.theme.colors.accent};
  margin-bottom: 2rem;
`;

const ItemsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  margin-bottom: 1rem;
`;

const ItemsHeader = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 0.75rem 0;
  color: ${props => props.theme.colors.primary};
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  &:last-child { border-bottom: none; }
`;

const ItemName = styled.div`
  color: ${props => props.theme.colors.primary};
`;

const ItemQty = styled.div`
  color: ${props => props.theme.colors.secondary};
  text-align: right;
`;

const ItemTotal = styled.div`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-align: right;
`;

const ModifierInfo = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
  margin-top: 0.25rem;
  grid-column: 1 / -1;
`;

const DiscountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  margin-bottom: 1rem;
`;

const DiscountHeader = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.colors.primary};
`;

const DiscountInputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const DiscountInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ApplyDiscountButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AppliedDiscounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AppliedDiscount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.9rem;
`;

const RemoveDiscountButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  cursor: pointer;
  
  &:hover {
    background: #cc0000;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.1rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.accent};
  }
`;

const SummaryLabel = styled.span`
  color: ${props => props.theme.colors.secondary};
`;

const SummaryValue = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
  border: 1px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const PaymentDetails = ({ 
  totalAmount,
  customerDetails,
  basketId,
  locationId,
  onPaymentSuccess,
  onPaymentFailure,
  venposBasket,
  appliedSaverCode,
  onBasketUpdate
}) => {
  const [errors, setErrors] = useState({});
  const [paymentSession, setPaymentSession] = useState(null);
  const [initializingPayment, setInitializingPayment] = useState(false);
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [localBasketData, setLocalBasketData] = useState(null);
  const [lastPaymentAmount, setLastPaymentAmount] = useState(null);
  const paymentMethod = 'payment_sense'; // Always Payment Sense
  
  const { getSubtotal, getGroupDiscountAmount, calculateTotal, getSelectedTicketsCount, selectedTickets, selectedDate, selectedTimeSlot, pageId, modifierSelections } = useBooking();
  const subtotal = getSubtotal();
  const saver = getGroupDiscountAmount();
  const grandTotal = calculateTotal();
  const ticketCount = getSelectedTicketsCount();
  
  // Normalize basket data shape (localBasketData may be raw data, venposBasket is { data })
  const currentBasket = localBasketData || venposBasket;
  const basketData = currentBasket?.data ? currentBasket.data : (currentBasket || null);
  const finalAmount = (basketData?.finalPrice ?? totalAmount ?? grandTotal);
  
  // Debug paymentSession when it changes
  useEffect(() => {
    if (paymentSession) {


    }
  }, [paymentSession]);

  // Initialize Payment Sense when component loads
  useEffect(() => {
    const initializePaymentSense = async () => {
      // Prevent multiple initializations
      if (!customerDetails || !basketId || !locationId || initializingPayment || paymentInitialized || initializationAttempted) {

        return;
      }

      setInitializingPayment(true);
      setInitializationAttempted(true);
      
      // Add a small delay to prevent ResizeObserver issues
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Create payment request with real selected tickets and pageId for GetBasketFinalPrice
        const paymentRequest = await venposService.createPaymentRequest(
          locationId,
          basketId,
          customerDetails,
          finalAmount,
          selectedTickets,
          selectedDate,
          pageId,  // Pass real pageId instead of using basketId
          modifierSelections  // Pass modifier selections
        );

        if (paymentRequest.isError) {

          setErrors({ payment: paymentRequest.error });
          setInitializingPayment(false);
          return;
        }

        // Store payment session parameters for Payment Sense only
        const paymentSession = {
          // Common
          orderId: paymentRequest.data.orderId,
          amount: finalAmount,
          currency: 'GBP',

          // Payment Sense
          paymentSenseCheckoutUrl: paymentRequest.data.paymentSenseCheckoutUrl,
          paymentSenseJWT: paymentRequest.data.paymentSenseJWT,
          paymentSenseJWT_ALT: paymentRequest.data.paymentSenseJWT_ALT,
          paymentSenseMerchantUrl: paymentRequest.data.paymentSenseMerchantUrl,
          paymentSenseMerchantUrl_ALT: paymentRequest.data.paymentSenseMerchantUrl_ALT,
          paymentSense_ApiUrl: paymentRequest.data.paymentSense_ApiUrl
        };

        setPaymentSession(paymentSession);
        setLastPaymentAmount(finalAmount);
        setPaymentInitialized(true);
        setInitializingPayment(false);

      } catch (error) {

        setErrors({ payment: 'Failed to initialize payment. Please refresh and try again.' });
        setInitializingPayment(false);
      }
    };

    // Only initialize once when all conditions are met
    if (paymentMethod === 'payment_sense' && finalAmount > 0 && customerDetails && basketId && locationId && !paymentInitialized && !initializingPayment && !initializationAttempted) {

      initializePaymentSense();
    }
  }, [paymentMethod, finalAmount, basketId, locationId, initializationAttempted, paymentInitialized]); // Removed customerDetails to prevent loops

  // If the basket total changes after initialization (e.g., discount add/remove), refresh the Payment Sense session
  useEffect(() => {
    if (!paymentInitialized) return;
    if (lastPaymentAmount === null) return;
    if (typeof finalAmount !== 'number') return;
    if (finalAmount === lastPaymentAmount) return;

    // Reset flags to allow re-initialization; delay a tick to avoid race
    setTimeout(() => {
      setPaymentInitialized(false);
      setInitializationAttempted(false);
      setPaymentSession(null);
      setLastPaymentAmount(null);
    }, 0);
  }, [finalAmount]);


  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !basketId) return;
    
    setApplyingDiscount(true);
    try {

      // Prepare current basket items and modifiers for refresh
      // CRITICAL: Preserve full datetime to maintain timeslot lock
      // Construct full datetime using selectedDate + selectedTimeSlot
      let selectedDateTime = '';
      if (selectedDate && selectedTimeSlot) {
        const dateStr = selectedDate.toISOString().split('T')[0]; // "2025-11-30"
        const timeStr = selectedTimeSlot.time || selectedTimeSlot.startDateTime?.split('T')[1] || '10:00:00';
        selectedDateTime = `${dateStr}T${timeStr}`;
      } else if (selectedDate) {
        selectedDateTime = selectedDate.toISOString().replace('Z', '');
      }
      
      const currentItems = selectedTickets?.map(ticket => ({
        pageType: 'WebPage',
        pageId: pageId,
        webSaleItemId: ticket.webSaleItemId,
        webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
        quantity: ticket.quantity,
        selectedDate: selectedDateTime, // Use full datetime, not just date
        fixedDonationAmount: 0
      })) || [];

      const currentModifiers = [];
      if (modifierSelections && Object.keys(modifierSelections).length > 0) {
        Object.entries(modifierSelections).forEach(([itemId, modifierData]) => {
          currentModifiers.push({
            webSaleItemId: itemId,
            productModifierId: parseInt(modifierData.modifierId),
            productModifierTextId: modifierData.modifierTextId,
            quantity: 1
          });
        });
      }
      
      // Get currently applied discount codes and add the new one
      const currentDiscountCodes = appliedDiscounts.map(d => d.code);
      const allDiscountCodes = [...currentDiscountCodes, discountCode.trim()];
      
      // Use the new refresh method instead of ApplyVoucher
      const result = await venposService.refreshBasketWithDiscounts(
        locationId, 
        basketId, 
        currentItems, 
        currentModifiers, 
        allDiscountCodes
      );
      
      if (result.isError) {

        setErrors({ discount: result.error || 'Invalid discount code' });
      } else {

        // Sync appliedDiscounts state with actual basket discounts
        const actualDiscounts = result.data?.discounts || [];
        setAppliedDiscounts(actualDiscounts.map(d => ({ code: d.code, result: d })));
        
        setDiscountCode('');
        setErrors({ discount: null });
        
        // Store the updated basket data locally to update UI totals
        setLocalBasketData(result.data);

        // Update parent component with the new basket data
        if (onBasketUpdate) {
          onBasketUpdate(result.data);
        }
      }
    } catch (error) {

      setErrors({ discount: 'Failed to apply discount code. Please try again.' });
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = async (discountCodeToRemove) => {
    try {

      // Prepare current basket items and modifiers for refresh
      // CRITICAL: Preserve full datetime to maintain timeslot lock
      // Construct full datetime using selectedDate + selectedTimeSlot
      let selectedDateTime = '';
      if (selectedDate && selectedTimeSlot) {
        const dateStr = selectedDate.toISOString().split('T')[0]; // "2025-11-30"
        const timeStr = selectedTimeSlot.time || selectedTimeSlot.startDateTime?.split('T')[1] || '10:00:00';
        selectedDateTime = `${dateStr}T${timeStr}`;
      } else if (selectedDate) {
        selectedDateTime = selectedDate.toISOString().replace('Z', '');
      }
      
      const currentItems = selectedTickets?.map(ticket => ({
        pageType: 'WebPage',
        pageId: pageId,
        webSaleItemId: ticket.webSaleItemId,
        webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
        quantity: ticket.quantity,
        selectedDate: selectedDateTime, // Use full datetime, not just date
        fixedDonationAmount: 0
      })) || [];

      const currentModifiers = [];
      if (modifierSelections && Object.keys(modifierSelections).length > 0) {
        Object.entries(modifierSelections).forEach(([itemId, modifierData]) => {
          currentModifiers.push({
            webSaleItemId: itemId,
            productModifierId: parseInt(modifierData.modifierId),
            productModifierTextId: modifierData.modifierTextId,
            quantity: 1
          });
        });
      }
      
      // Get remaining discounts (excluding the one to remove)
      const remainingDiscounts = appliedDiscounts
        .filter(d => d.code !== discountCodeToRemove)
        .map(d => d.code);
      
      // Refresh basket without the removed discount
      const result = await venposService.refreshBasketWithDiscounts(
        locationId, 
        basketId, 
        currentItems, 
        currentModifiers, 
        remainingDiscounts
      );
      
      if (result.isError) {

        setErrors({ discount: 'Failed to remove discount code. Please try again.' });
      } else {

        // Sync appliedDiscounts state with actual basket discounts
        const actualDiscounts = result.data?.discounts || [];
        setAppliedDiscounts(actualDiscounts.map(d => ({ code: d.code, result: d })));
        
        setErrors({ discount: null });
        
        // Store the updated basket data locally to update UI totals
        setLocalBasketData(result.data);

        // Update parent component with the new basket data
        if (onBasketUpdate) {
          onBasketUpdate(result.data);
        }
      }
    } catch (error) {

      setErrors({ discount: 'Failed to remove discount code. Please try again.' });
    }
  };

  const handlePaymentSenseSuccess = (paymentResult) => {

    if (onPaymentSuccess) {
      onPaymentSuccess(paymentResult);
    }
  };

  const handlePaymentSenseFailure = (error) => {

    setErrors({ payment: error.error || 'Payment failed. Please try again.' });
    if (onPaymentFailure) {
      onPaymentFailure(error);
    }
  };

  const handlePaymentSenseCancel = () => {

    setErrors({ payment: 'Payment was cancelled. You can try again.' });
  };


  return (
    <PaymentContainer>
      <SectionTitle>
        💳 Payment Details
      </SectionTitle>

      {/* Discount Code Section */}
      <DiscountSection>
        <DiscountHeader>🎫 Discount Codes</DiscountHeader>
        
        <DiscountInputContainer>
          <DiscountInput
            type="text"
            placeholder="Enter discount code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
            disabled={applyingDiscount}
          />
          <ApplyDiscountButton
            onClick={handleApplyDiscount}
            disabled={!discountCode.trim() || applyingDiscount}
          >
            {applyingDiscount ? 'Applying...' : 'Apply'}
          </ApplyDiscountButton>
        </DiscountInputContainer>

        {errors.discount && (
          <div style={{ color: '#ff4444', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {errors.discount}
          </div>
        )}

        {appliedDiscounts.length > 0 && (
          <AppliedDiscounts>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Applied Discounts:
            </div>
            {appliedDiscounts.map((discount, index) => (
              <AppliedDiscount key={index}>
                <span>{discount.code}</span>
                <RemoveDiscountButton onClick={() => handleRemoveDiscount(discount.code)}>
                  Remove
                </RemoveDiscountButton>
              </AppliedDiscount>
            ))}
          </AppliedDiscounts>
        )}

        {appliedSaverCode && (
          <div style={{ fontSize: '0.9rem', color: '#28a745', marginTop: '0.5rem' }}>
            ✅ Family Saver discount automatically applied
          </div>
        )}
      </DiscountSection>

      {/* Payment Sense Integration */}
      {paymentMethod === 'payment_sense' && finalAmount > 0 && (
        <PaymentSection style={{ padding: '0', background: 'transparent', border: 'none', boxShadow: 'none' }}>

          {errors.payment && (
            <ErrorMessage style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '8px', background: '#f8f9fa' }}>
              {errors.payment}
            </ErrorMessage>
          )}

          {initializingPayment && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <div style={{ marginBottom: '1rem' }}>🔄 Initializing secure payment...</div>
              <div style={{ fontSize: '0.9rem' }}>Please wait while we set up your payment session.</div>
            </div>
          )}

          {paymentSession && !initializingPayment && (
            <PaymentSenseConnectE
              paymentSession={paymentSession}
              onPaymentSuccess={handlePaymentSenseSuccess}
              onPaymentFailure={handlePaymentSenseFailure}
              onPaymentCancel={handlePaymentSenseCancel}
            />
          )}

          {!paymentSession && !initializingPayment && !errors.payment && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <div style={{ marginBottom: '1rem' }}>⚠️ Payment not initialized</div>
              <div style={{ fontSize: '0.9rem' }}>Please ensure all details are filled and try refreshing the page.</div>
            </div>
          )}
        </PaymentSection>
      )}

      {/* No payment required (free/fully discounted) */}
      {paymentMethod === 'payment_sense' && finalAmount <= 0 && (
        <PaymentSection style={{ padding: '1rem', background: 'transparent', border: 'none', boxShadow: 'none' }}>
          <div style={{ padding: '1rem', textAlign: 'center', color: '#2e7d32', background: '#e8f5e9', borderRadius: '8px' }}>
            <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>No payment required</div>
            <div style={{ fontSize: '0.95rem' }}>Your total is £0.00 after discounts.</div>
          </div>
        </PaymentSection>
      )}


      {basketData?.groups && basketData.groups.length > 0 ? (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          {basketData.groups.map((grp, gi) => (
            <div key={gi}>
              {grp?.name && <div style={{ fontWeight: 600, margin: '0.25rem 0 0.5rem' }}>{grp.name}</div>}
              {(grp.items || []).map((it, ii) => {
                const qty = Number(it.quantity || 0);
                // Use discountedAmount if available (from Venpos API), otherwise fall back to totalPrice or calculated price
                const line = typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                           (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * qty));
                
                // Get modifier information for this item
                const itemModifiers = modifierSelections[it.webSaleItemId];
                const modifierText = itemModifiers ? ` • ${itemModifiers.modifierName}` : '';
                
                return (
                  <ItemRow key={ii}>
                    <ItemName>
                      {it.name}
                      {modifierText && <ModifierInfo>{modifierText}</ModifierInfo>}
                    </ItemName>
                    <ItemQty>x{qty}</ItemQty>
                    <ItemTotal>£{line.toFixed(2)}</ItemTotal>
                  </ItemRow>
                );
              })}
            </div>
          ))}
        </ItemsSection>
      ) : venposBasket?.data?.groups && venposBasket.data.groups.length > 0 ? (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          {venposBasket.data.groups.map((grp, gi) => (
            <div key={gi}>
              {grp?.name && <div style={{ fontWeight: 600, margin: '0.25rem 0 0.5rem' }}>{grp.name}</div>}
              {(grp.items || []).map((it, ii) => {
                const qty = Number(it.quantity || 0);
                // Use discountedAmount if available (from Venpos API), otherwise fall back to totalPrice or calculated price
                const line = typeof it.discountedAmount === 'number' ? it.discountedAmount : 
                           (typeof it.totalPrice === 'number' ? it.totalPrice : (Number(it.price || 0) * qty));
                
                // Get modifier information for this item
                const itemModifiers = modifierSelections[it.webSaleItemId];
                const modifierText = itemModifiers ? ` • ${itemModifiers.modifierName}` : '';
                
                return (
                  <ItemRow key={ii}>
                    <ItemName>
                      {it.name}
                      {modifierText && <ModifierInfo>{modifierText}</ModifierInfo>}
                    </ItemName>
                    <ItemQty>x{qty}</ItemQty>
                    <ItemTotal>£{line.toFixed(2)}</ItemTotal>
                  </ItemRow>
                );
              })}
            </div>
          ))}
        </ItemsSection>
      ) : (
        <ItemsSection>
          <ItemsHeader>Your Tickets</ItemsHeader>
          <div style={{ padding: '1rem', fontStyle: 'italic', color: '#666' }}>
            Loading basket items...
          </div>
        </ItemsSection>
      )}

      <OrderSummary>
        <SectionHeader>
          Total ({ticketCount} ticket{ticketCount !== 1 ? 's' : ''})
        </SectionHeader>
        
        {basketData ? (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{(basketData.totalPrice || subtotal).toFixed(2)}</SummaryValue>
            </SummaryRow>
            {basketData.discounts && basketData.discounts.length > 0 && (
              <>
                {basketData.discounts.map((discount, index) => (
                  <SummaryRow key={index}>
                    <SummaryLabel>{discount.code}</SummaryLabel>
                    <SummaryValue>-£{(discount.discountedAmount || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                ))}
              </>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{(basketData.finalPrice ?? grandTotal).toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        ) : venposBasket?.data ? (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{(venposBasket.data.totalPrice || subtotal).toFixed(2)}</SummaryValue>
            </SummaryRow>
            {venposBasket.data.discounts && venposBasket.data.discounts.length > 0 && (
              <>
                {venposBasket.data.discounts.map((discount, index) => (
                  <SummaryRow key={index}>
                    <SummaryLabel>{discount.code}</SummaryLabel>
                    <SummaryValue>-£{(discount.discountedAmount || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                ))}
              </>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{(venposBasket.data.finalPrice ?? grandTotal).toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        ) : (
          <>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue>£{subtotal.toFixed(2)}</SummaryValue>
            </SummaryRow>
            {saver > 0 && (
              <SummaryRow>
                <SummaryLabel>Family Saver</SummaryLabel>
                <SummaryValue>-£{saver.toFixed(2)}</SummaryValue>
              </SummaryRow>
            )}
            <SummaryRow>
              <SummaryLabel>Today</SummaryLabel>
              <SummaryValue>£{grandTotal.toFixed(2)}</SummaryValue>
            </SummaryRow>
          </>
        )}
      </OrderSummary>

      <InfoBox>
        <InfoText>
          🔒 Your payment is processed securely. We accept all major credit and debit cards. 
          Your card details are encrypted and never stored on our servers.
        </InfoText>
      </InfoBox>
    </PaymentContainer>
  );
};

export default PaymentDetails;